import React from 'react';
import { GitBranch, ArrowRight, Check, Loader2 } from 'lucide-react';

const DecisionCard = ({ card, onOptionSelect, disabled = false, selectedOptionId = null, clickingOptionId = null, demoMode = false }) => {
  // In demo mode, show normal styling but prevent clicks
  const { title, subtitle, options = [] } = card;
  const selectedOption = selectedOptionId ? options.find(opt => opt.id === selectedOptionId) : null;
  // Only collapse if an option is selected AND that option exists in the current card
  // This prevents empty cards when ID conflicts occur across different cards
  const isCollapsed = selectedOptionId !== null && !!selectedOption;

  return (
    <div className="relative overflow-hidden dark:bg-gray-800 bg-white border border-gray-200 dark:border-gray-700 rounded-2xl">
      {/* Header Section - icon left pattern */}
      <div className="relative flex items-start gap-5 sm:gap-6 p-4">
        {/* Icon column */}
        <div className="flex-shrink-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/50 dark:to-indigo-800/30 text-indigo-600 dark:text-indigo-400 shadow-sm">
            <GitBranch size={24} strokeWidth={2.5} />
          </div>
        </div>
        
        {/* Title/description */}
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] sm:text-base font-semibold leading-tight text-gray-900 dark:text-gray-100">
            {title || 'Decision Needed'}
          </h3>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3">
              {subtitle}
            </p>
          )}
          {isCollapsed && selectedOption && (
            <div className="mt-4 w-full">
              <div className="flex items-center gap-4 px-4 py-3 border border-emerald-200 dark:border-emerald-700 rounded-2xl">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Check size={16} className="text-white" strokeWidth={3} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mt-0.5 text-[13px] text-gray-600 dark:text-gray-400 leading-snug line-clamp-3">
                    Selected
                  </div>
                  <div className="text-sm leading-tight font-medium text-black dark:text-white">
                    {selectedOption.label}
                  </div>
                  {selectedOption.description && (
                    <div className="text-sm text-black dark:text-white mt-1">
                      {selectedOption.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Options List - Compact horizontal layout */}
      {!isCollapsed && (
        <div className="relative px-4 pb-4 pt-1 flex flex-col gap-2">
          {options.map((option, idx) => {
            const isSelected = selectedOptionId === option.id;
            const isClicking = clickingOptionId === option.id;
            return (
              <button
                key={option.id || idx}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!disabled) {
                    onOptionSelect(option);
                  }
                }}
                disabled={disabled || isClicking}
                className={`
                  group relative flex items-center gap-3 px-3 py-3 text-left rounded-xl border transition-all duration-150
                  touch-manipulation
                  ${isSelected || isClicking
                    ? 'bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/50 dark:to-indigo-800/30 border-indigo-400 dark:border-indigo-600 ring-1 ring-indigo-300 dark:ring-indigo-700 shadow-md'
                    : (disabled && !demoMode)
                      ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700' 
                      : 'bg-indigo-50/30 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-800/50 hover:bg-indigo-100/50 dark:hover:bg-indigo-900/20 hover:border-indigo-300 dark:hover:border-indigo-700 active:bg-indigo-100 dark:active:bg-indigo-900/30 active:scale-[0.98] cursor-pointer'
                  }
                `}
              >
                {/* Letter badge - compact */}
                <span className={`
                  flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold transition-colors
                  ${isSelected
                    ? 'bg-indigo-500 dark:bg-indigo-600 text-white'
                    : isClicking
                      ? 'bg-indigo-400 dark:bg-indigo-500 text-white'
                      : (disabled && !demoMode)
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 group-active:bg-indigo-200 dark:group-active:bg-indigo-800'
                  }
                `}>
                  {isSelected ? (
                    <Check size={14} strokeWidth={3} />
                  ) : isClicking ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    String.fromCharCode(65 + idx)
                  )}
                </span>
                
                {/* Label + optional description */}
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-[14px] leading-tight ${isSelected || isClicking ? 'text-indigo-900 dark:text-indigo-100' : (disabled && !demoMode) ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                    {option.label}
                  </div>
                  {option.description && (
                    <div className={`text-xs leading-snug mt-0.5 ${isSelected || isClicking ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400'}`}>
                      {option.description}
                    </div>
                  )}
                </div>
                
                {/* Right chevron - always visible on mobile (no hover), enhanced on desktop hover */}
                {isSelected ? (
                  <Check size={16} className="flex-shrink-0 text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
                ) : isClicking ? (
                  <span className="text-xs text-indigo-600 dark:text-indigo-400">Selecting...</span>
                ) : (!disabled || demoMode) && (
                  <ArrowRight size={14} className="flex-shrink-0 text-indigo-300 dark:text-indigo-600 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DecisionCard;
